import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { saTrackPage } from './utils/tracker';
import { CRMProvider, useCRM } from './context/CRMContext';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import Pipeline from './pages/Pipeline';
import Followups from './pages/Followups';
import Campaigns from './pages/Campaigns';
import Integrations from './pages/Integrations';
import Settings from './pages/Settings';
import WebForms from './pages/WebForms';
import PublicForm from './pages/PublicForm';
import PsychometricTest from './pages/PsychometricTest';
import Login from './pages/Login';
import ActivityLog from './pages/ActivityLog';
import Reports from './pages/Reports';
import WhatsAppMonitor from './pages/WhatsAppMonitor';
import SuperAdmin from './pages/SuperAdmin';
import Assessments from './pages/Assessments';
import Universities from './pages/Universities';
import Engagement from './pages/Engagement';
import Documents from './pages/Documents';
import Applications from './pages/Applications';
import Meetings from './pages/Meetings';
import Leaves from './pages/Leaves';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminPortal from './pages/SuperAdminPortal';
import DigitalMedia from './pages/DigitalMedia';
import WorkReport from './pages/WorkReport';
import CounselingHub from './pages/CounselingHub';
import TestPrep from './pages/TestPrep';
import Automations from './pages/Automations';
import SubAgents from './pages/SubAgents';
import StudentForm from './pages/StudentForm';
import { canAccessPage as sharedCanAccessPage } from './utils/access';

function LoadingScreen() {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ede9fe 50%, #dbeafe 100%)',
      flexDirection: 'column', gap: 16,
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 50%, #06b6d4 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, fontWeight: 800, color: '#fff',
        boxShadow: '0 8px 32px rgba(124,58,237,0.35)',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}>C</div>
      <div style={{ fontSize: 13, color: '#64748b', fontFamily: "'Inter', sans-serif" }}>Loading your workspace...</div>
      <style>{'@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.95)} }'}</style>
    </div>
  );
}

function hexToThemeVars(primaryHex) {
  const hex = primaryHex.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const dr = Math.max(0, Math.floor(r * 0.7));
  const dg = Math.max(0, Math.floor(g * 0.7));
  const db = Math.max(0, Math.floor(b * 0.7));
  const dark = '#' + [dr, dg, db].map(n => n.toString(16).padStart(2, '0')).join('');
  const lr = Math.min(255, r + Math.floor((255 - r) * 0.45));
  const lg = Math.min(255, g + Math.floor((255 - g) * 0.45));
  const lb = Math.min(255, b + Math.floor((255 - b) * 0.45));
  const light = '#' + [lr, lg, lb].map(n => n.toString(16).padStart(2, '0')).join('');
  return { r, g, b, dark, light };
}

// Dark mode context
export const DarkModeContext = React.createContext({ dark: false, toggle: () => {} });
export function useDarkMode() { return React.useContext(DarkModeContext); }

function DarkModeProvider({ children }) {
  const [dark, setDark] = useState(() => localStorage.getItem('crm-theme') === 'dark');
  const toggle = () => setDark(d => {
    const next = !d;
    localStorage.setItem('crm-theme', next ? 'dark' : 'light');
    return next;
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }, [dark]);
  return <DarkModeContext.Provider value={{ dark, toggle }}>{children}</DarkModeContext.Provider>;
}

function ThemeInjector() {
  const { company } = useCRM();

  // ── Brand colour CSS vars ─────────────────────────────────────────────────
  useEffect(() => {
    const primary = company?.theme?.primary;
    if (!primary || !/^#[0-9a-fA-F]{6}$/.test(primary)) return;
    const { r, g, b, dark, light } = hexToThemeVars(primary);
    const el = document.getElementById('company-theme') || document.createElement('style');
    el.id = 'company-theme';
    el.textContent = ':root {'
      + '--color-primary:' + primary + ';'
      + '--color-primary-dark:' + dark + ';'
      + '--color-primary-light:rgba(' + r + ',' + g + ',' + b + ',0.15);'
      + '--color-primary-soft:rgba(' + r + ',' + g + ',' + b + ',0.08);'
      + '--color-primary-glow:rgba(' + r + ',' + g + ',' + b + ',0.40);'
      + '--color-primary-focus:rgba(' + r + ',' + g + ',' + b + ',0.12);'
      + '--color-primary-text:' + light + ';'
      + '}';
    if (!el.parentNode) document.head.appendChild(el);
  }, [company?.theme?.primary]); // eslint-disable-line

  // ── Per-org favicon + tab title (white-label) ─────────────────────────────
  useEffect(() => {
    const hostname = window.location.hostname;
    const defaultHosts = ['localhost', 'crm.clarioneducation.in', '127.0.0.1'];
    const isCustomDomain = !defaultHosts.some(h => hostname === h || hostname.endsWith(h));
    if (!isCustomDomain || !company) return;

    if (company.logoUrl) {
      document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = company.logoUrl; // base64 data URL set by super admin
      document.head.appendChild(favicon);
    }
    if (company.theme?.appName || company.name) {
      document.title = company.theme?.appName || company.name;
    }
  }, [company?.logoUrl, company?.name]); // eslint-disable-line

  return null;
}

function AppContent() {
  const { currentPage, loading: crmLoading, currentUser, company } = useCRM();

  useEffect(() => {
    if (currentPage && currentUser?.id) {
      saTrackPage(currentPage);
    }
  }, [currentPage, currentUser?.id]); // eslint-disable-line

  if (crmLoading) return <LoadingScreen />;

  const canAccessPage = (pageId) => sharedCanAccessPage(currentUser, company, pageId);

  const PAGES = {
    dashboard:    <Dashboard />,
    leads:        <Leads />,
    pipeline:     <Pipeline />,
    followups:    <Followups />,
    campaigns:    <Campaigns />,
    integrations: <Integrations />,
    webforms:     <WebForms />,
    settings:     <Settings />,
    activitylog:  <ActivityLog />,
    assessments:  <Assessments />,
    superadmin:   <SuperAdmin />,
    reports:      <Reports />,
    wamonitor:    <WhatsAppMonitor />,
    universities: <Universities />,
    engagement:   <Engagement />,
    documents:    <Documents />,
    applications: <Applications />,
    meetings:     <Meetings />,
    leaves:       <Leaves />,
    digitalmedia:    <DigitalMedia />,
    workreport:      <WorkReport />,
    counselinghub:   <CounselingHub />,
    testprep:        <TestPrep />,
    automations:     <Automations />,
    subagents:       <SubAgents company={company} />,
  };

  const activePage = PAGES[currentPage];
  const pageAllowed = !currentPage || canAccessPage(currentPage);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-primary)' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Header />
        <main style={{ flex: 1, overflow: 'hidden' }}>
          {pageAllowed
            ? (activePage || <Dashboard />)
            : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                <div style={{ fontSize: 40 }}>&#x1F510;</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Access Restricted</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>You do not have permission to view this page. Contact your admin.</div>
              </div>
            )
          }
        </main>
      </div>
    </div>
  );
}

function NoCompanyScreen({ removed }) {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ede9fe 50%, #dbeafe 100%)',
      flexDirection: 'column', gap: 12, textAlign: 'center', padding: 24,
      fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{ fontSize: 40 }}>{removed ? '&#x1F6AB;' : '&#x1F3E2;'}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
        {removed ? 'Access Revoked' : 'No Company Assigned'}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', maxWidth: 360 }}>
        {removed
          ? 'Your access to this CRM has been revoked by an administrator. Please contact your company admin if you believe this is a mistake.'
          : "Your account isn't linked to any organization yet. Please ask your company admin to invite you again, or contact support."}
      </div>
      <button
        onClick={() => { import('./firebase/auth').then(m => m.logout()); }}
        style={{ marginTop: 12, padding: '10px 24px', borderRadius: 10, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
      >
        Sign Out
      </button>
    </div>
  );
}

function AuthGate() {
  const { firebaseUser, userProfile, loading, profileLoading } = useAuth();
  if (loading || profileLoading) return <LoadingScreen />;
  if (!firebaseUser) return <Login />;

  if (userProfile?.role === 'superadmin') {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        flexDirection: 'column', gap: 16, textAlign: 'center', padding: 24,
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{ fontSize: 48, marginBottom: 4 }}>&#x1F512;</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>Super Admin Account</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', maxWidth: 340, lineHeight: 1.7 }}>
          Super admin accounts cannot access company portals. Please use the dedicated Super Admin Portal.
        </div>
        <a
          href="/superadmin"
          style={{
            marginTop: 8, padding: '12px 28px', borderRadius: 11, border: 'none',
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14,
            textDecoration: 'none', display: 'inline-block',
            boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
          }}
        >
          Go to Super Admin Portal
        </a>
      </div>
    );
  }

  if (userProfile && userProfile.status === 'removed') {
    return <NoCompanyScreen removed />;
  }
  if (userProfile && !userProfile.companyId) {
    return <NoCompanyScreen />;
  }
  return (
    <CRMProvider>
      <ThemeInjector />
      <AppContent />
    </CRMProvider>
  );
}

function SuperAdminGate() {
  const { firebaseUser, loading, profileLoading, userProfile } = useAuth();

  if (loading || profileLoading) return <LoadingScreen />;
  if (!firebaseUser) {
    return <SuperAdminLogin onSuccess={() => {}} />;
  }
  if (userProfile?.role === 'superadmin') {
    return <SuperAdminPortal currentUser={userProfile} />;
  }
  if (!userProfile) return <LoadingScreen />;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f172a, #1e293b)', fontFamily: 'sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F512;</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Access Denied</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>This account is not a super admin.</div>
        <button onClick={() => import('./firebase/auth').then(m => m.logout())}
          style={{ marginTop: 20, padding: '10px 24px', borderRadius: 10, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const publicFormId = urlParams.get('form');
  if (publicFormId) return <PublicForm formId={publicFormId} />;
  const publicAssessmentId = urlParams.get('assessment');
  if (publicAssessmentId) return <PsychometricTest assessmentId={publicAssessmentId} />;
  const pathParts = window.location.pathname.split('/');
  if (pathParts[1] === 'assess' && pathParts[2]) return <PsychometricTest assessmentId={pathParts[2]} />;
  if (pathParts[1] === 'student-form' && pathParts[2] && pathParts[3] && pathParts[4]) {
    return <StudentForm />;
  }
  if (pathParts[1] === 'superadmin') {
    return (
      <AuthProvider>
        <SuperAdminGate />
      </AuthProvider>
    );
  }
  return (
    <DarkModeProvider>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </DarkModeProvider>
  );
}
